import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { fetchJson } from "../lib/api";
import { useHabits } from "../hooks/queries";
import { TerminalLine } from "../components/terminal/TerminalLine";
import { todayIST } from "../lib/time";

export function CalendarPage() {
  const navigate = useNavigate();
  const today = todayIST();
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [selectedDateStr, setSelectedDateStr] = useState(() => today);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  // Fetch heatmap for cell colors
  const { data: heatmapData } = useQuery({
    queryKey: ["heatmap", year],
    queryFn: () => fetchJson<{ cells: Array<{ date: string; count: number; total: number }> }>(`/api/stats/heatmap?year=${year}`),
    staleTime: 60_000,
  });

  const cellsMap = new Map<string, { count: number; total: number }>();
  if (heatmapData?.cells) {
    for (const c of heatmapData.cells) {
      cellsMap.set(c.date, { count: c.count, total: c.total });
    }
  }

  // Fetch events for selected day
  const { data: calendarData, isLoading: loadingCalendar } = useQuery({
    queryKey: ["calendar-events", selectedDateStr],
    queryFn: () => fetchJson<{ events: Array<{ id: string; summary: string; start: string; end: string; allDay: boolean }> }>(`/api/calendar/events?date=${selectedDateStr}`),
    enabled: !!selectedDateStr,
    staleTime: 30_000,
  });

  // Fetch completions for selected day
  const { data: completionsData, isLoading: loadingCompletions } = useQuery({
    queryKey: ["completions", "day", selectedDateStr],
    queryFn: () => fetchJson<Array<{ habit_id: string; date: string; value: number }>>(`/api/completions?from=${selectedDateStr}&to=${selectedDateStr}`),
    enabled: !!selectedDateStr,
    staleTime: 10_000,
  });

  const habits = useHabits();
  const allHabits = habits.data ?? [];

  // Compute active habits for selected date
  const activeHabits = allHabits.filter(h => {
    if (h.is_one_off) {
      return h.target_date === selectedDateStr;
    }
    return h.created_at.slice(0, 10) <= selectedDateStr;
  });

  const completionsMap = new Map(completionsData?.map(c => [c.habit_id, c]) ?? []);

  const completedList = activeHabits.filter(h => {
    const comp = completionsMap.get(h.id);
    if (!comp) return false;
    return h.type === "boolean" || comp.value >= h.target_value;
  });

  const pendingList = activeHabits.filter(h => {
    const comp = completionsMap.get(h.id);
    if (!comp) return true;
    return h.type !== "boolean" && comp.value < h.target_value;
  });

  // Calendar calculations
  const firstDay = new Date(year, month, 1).getDay();
  const totalDays = new Date(year, month + 1, 0).getDate();

  const daysArray: Array<{ dateStr: string; dayNum: number; isCurrentMonth: boolean }> = [];

  // Previous month padding
  const prevMonthTotalDays = new Date(year, month, 0).getDate();
  for (let i = firstDay - 1; i >= 0; i--) {
    const d = prevMonthTotalDays - i;
    const pm = month === 0 ? 11 : month - 1;
    const py = month === 0 ? year - 1 : year;
    const dateStr = `${py}-${String(pm + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    daysArray.push({ dateStr, dayNum: d, isCurrentMonth: false });
  }

  // Current month days
  for (let d = 1; d <= totalDays; d++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    daysArray.push({ dateStr, dayNum: d, isCurrentMonth: true });
  }

  // Next month padding to align rows
  const nextDays = 42 - daysArray.length;
  for (let d = 1; d <= nextDays; d++) {
    const nm = month === 11 ? 0 : month + 1;
    const ny = month === 11 ? year + 1 : year;
    const dateStr = `${ny}-${String(nm + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    daysArray.push({ dateStr, dayNum: d, isCurrentMonth: false });
  }

  const handlePrevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem", fontFamily: "var(--font-mono)" }}>
      <TerminalLine prompt accent>calendar</TerminalLine>

      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "1.25rem" }}>
        {/* Calendar View Card */}
        <div
          className="shukan-card"
          style={{
            border: "1px solid var(--border)",
            backgroundColor: "var(--bg-elevated)",
            padding: "1.25rem",
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          {/* Calendar Month Selector Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontWeight: "bold", fontSize: "1rem", color: "var(--accent)" }}>
              &gt; {monthNames[month]} {year}
            </span>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button type="button" className="shukan-btn" onClick={handlePrevMonth}>
                &lt; prev
              </button>
              <button type="button" className="shukan-btn" onClick={() => setCurrentDate(new Date())}>
                today
              </button>
              <button type="button" className="shukan-btn" onClick={handleNextMonth}>
                next &gt;
              </button>
            </div>
          </div>

          {/* Weekday labels */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              textAlign: "center",
              fontSize: "0.75rem",
              fontWeight: "bold",
              color: "var(--fg-dim)",
              borderBottom: "1px dashed var(--border)",
              paddingBottom: "0.5rem",
            }}
          >
            <div>Sun</div>
            <div>Mon</div>
            <div>Tue</div>
            <div>Wed</div>
            <div>Thu</div>
            <div>Fri</div>
            <div>Sat</div>
          </div>

          {/* Monthly grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: "4px",
            }}
          >
            {daysArray.map((day, idx) => {
              const info = cellsMap.get(day.dateStr);
              const count = info?.count ?? 0;
              const total = info?.total ?? 1;
              const percent = total > 0 ? count / total : 0;
              const isSelected = selectedDateStr === day.dateStr;

              // Color cell bg
              let cellBg = "rgba(255, 255, 255, 0.02)";
              if (day.isCurrentMonth && count > 0) {
                if (percent <= 0.25) cellBg = "color-mix(in srgb, var(--accent) 20%, var(--bg-elevated))";
                else if (percent <= 0.50) cellBg = "color-mix(in srgb, var(--accent) 45%, var(--bg-elevated))";
                else if (percent <= 0.75) cellBg = "color-mix(in srgb, var(--accent) 70%, var(--bg-elevated))";
                else cellBg = "color-mix(in srgb, var(--accent) 90%, var(--bg-elevated))";
              }

              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setSelectedDateStr(day.dateStr)}
                  style={{
                    height: "3.5rem",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    padding: "0.3rem",
                    background: cellBg,
                    border: isSelected
                      ? "2px solid var(--accent)"
                      : "1px solid var(--border)",
                    borderRadius: "2px",
                    cursor: "pointer",
                    opacity: day.isCurrentMonth ? 1 : 0.3,
                    color: isSelected ? "var(--accent)" : "var(--fg)",
                    boxShadow: isSelected ? "0 0 8px var(--accent-glow)" : "none",
                  }}
                >
                  <span style={{ fontSize: "0.75rem", fontWeight: isSelected ? "bold" : "normal" }}>
                    {day.dayNum}
                  </span>
                  {day.isCurrentMonth && count > 0 && (
                    <span style={{ fontSize: "0.6rem", color: "var(--accent)", alignSelf: "flex-end" }}>
                      {count}/{total}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected Day Details Panel */}
        <div
          className="shukan-card"
          style={{
            border: "1px solid var(--border)",
            backgroundColor: "var(--bg-elevated)",
            padding: "1.25rem",
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          <TerminalLine prompt accent>
            <strong>details: {selectedDateStr}</strong>
          </TerminalLine>

          {/* Google Calendar Events Section */}
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <span style={{ fontSize: "0.75rem", color: "var(--accent)", fontWeight: "bold" }}>
              &gt; Google Calendar Events
            </span>
            {loadingCalendar ? (
              <span style={{ fontSize: "0.8rem", color: "var(--fg-dim)" }}>loading...</span>
            ) : calendarData?.events && calendarData.events.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                {calendarData.events.map((e) => (
                  <div
                    key={e.id}
                    style={{
                      fontSize: "0.8rem",
                      padding: "0.35rem 0.5rem",
                      borderLeft: "2px solid var(--accent)",
                      backgroundColor: "rgba(255,255,255,0.02)",
                    }}
                  >
                    <div>{e.summary}</div>
                    <div style={{ fontSize: "0.7rem", color: "var(--fg-dim)" }}>
                      {e.allDay ? "All Day" : `${e.start.slice(11, 16)} - ${e.end.slice(11, 16)}`}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <span style={{ fontSize: "0.8rem", color: "var(--fg-dim)", fontStyle: "italic" }}>
                $ no calendar events scheduled or calendar not connected.
              </span>
            )}
          </div>

          {/* Habit Completions Section */}
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", borderTop: "1px dashed var(--border)", paddingTop: "0.75rem" }}>
            <span style={{ fontSize: "0.75rem", color: "var(--accent)", fontWeight: "bold" }}>
              &gt; Habit Completions
            </span>
            {loadingCompletions ? (
              <span style={{ fontSize: "0.8rem", color: "var(--fg-dim)" }}>loading...</span>
            ) : activeHabits.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                {completedList.map((h) => (
                  <div key={h.id} style={{ fontSize: "0.8rem", color: "var(--success)" }}>
                    ✓ {h.icon} {h.name} (+{h.xp_value}xp)
                  </div>
                ))}
                {pendingList.map((h) => {
                  const comp = completionsMap.get(h.id);
                  return (
                    <div key={h.id} style={{ fontSize: "0.8rem", color: "var(--fg-dim)" }}>
                      · {h.icon} {h.name} {h.type !== "boolean" ? `(${comp?.value ?? 0}/${h.target_value})` : ""}
                    </div>
                  );
                })}
              </div>
            ) : (
              <span style={{ fontSize: "0.8rem", color: "var(--fg-dim)", fontStyle: "italic" }}>
                $ no habits active on this day.
              </span>
            )}
          </div>

          {/* Navigation Action */}
          <button
            type="button"
            className="shukan-btn shukan-btn-primary"
            style={{ marginTop: "0.5rem" }}
            onClick={() => navigate(`/?date=${selectedDateStr}`)}
          >
            $ load this date on dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
